import { flatMap } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useViewerRoles } from '@treams/treams-hooks';
import { CircleUserIcon, CityIcon, GroupsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import { Container, Jumbo, Sidebar, SidebarLayout, Skeleton, tokens } from '@treams/treams-ui';

import GoalsTabs from '../GoalsTabs';
import { CollectiveGoalsSidebarSkeleton } from './CollectiveGoalsSidebarSkeleton';
import { CollectiveGoalsContext, CollectiveGoalsList, GroupGoals } from './Common';

const GroupStructureOffset = styled(Box)<{ offset: number }>`
	margin-left: ${(props) => props.offset * 24}px;
	position: relative;

	${(props) =>
		props.offset !== 0 &&
		`
		:before {
			content: '';
			display: block;
			position: absolute;
			width: 2px;
			height: 20px;
			top: 50%;
			margin-top: -10px;
			left: -8px;
			background-color: ${tokens.border.default};
			border-radius: ${tokens.radius.xs}px;
		}
	`}
`;

function GroupSidebarItem(props: {
	group: { id: string; name: string };
	onChange: (id: string) => void;
}) {
	const { data } = graphql.useGroupGoalCountsQuery({ variables: { id: props.group.id } });
	const { groupID } = useContext(CollectiveGoalsContext);

	return (
		<Sidebar.Item
			title={props.group.name}
			small
			badge={
				data ? (data?.group.goals.length === 0 ? '—' : String(data?.group.goals.length)) : '..'
			}
			overflow="hidden"
			onClick={() => props.onChange(props.group.id)}
			selected={groupID === props.group.id}
		/>
	);
}

type Props = { onChange: (id: string) => void };

const CollectiveGoalsSidebarByManagement = ({ onChange }: Props) => {
	const { t } = useTranslation();
	const { data, loading } = graphql.useViewerGroupsQuery({
		fetchPolicy: 'cache-first',
	});

	const managedGroups = data?.viewer.roles.filter(({ role, targetV2 }) => {
		if (targetV2?.__typename === 'Group') {
			const isManagerOrCoach =
				role.type === graphql.RoleType.PrimaryManager || role.type === graphql.RoleType.Coach;

			if (isManagerOrCoach) {
				return !targetV2.isArchived;
			}
		}

		return false;
	});

	return (
		<>
			<Sidebar.SectionTitle mt={4} mb={1}>
				{t('goals.team-and-department.section.managed.title')}
			</Sidebar.SectionTitle>
			{loading ? (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton small />
				</Skeleton.Repeat>
			) : (
				<>
					{managedGroups?.length ? (
						managedGroups?.map((group) => (
							<GroupSidebarItem
								key={group.targetV2!.id}
								group={group.targetV2 as graphql.Group}
								onChange={onChange}
							/>
						))
					) : (
						<CollectiveGoalsSidebarSkeleton
							icon={<CircleUserIcon />}
							message={t('goals.team-and-department.section.managed.noItems')}
						/>
					)}
				</>
			)}
		</>
	);
};

function CollectiveGoalsSidebarDepartments(props: { onChange: (id: string) => void }) {
	const { params } = useRouteMatch<{ id?: string }>();
	const { t } = useTranslation();
	const { onChange } = props;

	const { data, loading } = graphql.useViewerParentGroupsQuery({
		fetchPolicy: 'cache-first',
	});

	let groups = data ? flatMap(data.viewer.group.paths, (path) => path.groups).reverse() : undefined;
	groups = groups ? groups.concat([data!.viewer.group]) : undefined;

	useEffect(() => {
		if (!params.id && data) {
			onChange(data.viewer.group.id);
		}
	}, [params, data, onChange]);

	return (
		<>
			<Sidebar.SectionTitle mt={3} mb={1}>
				{t('goals.team-and-department.section.departments.title')}
			</Sidebar.SectionTitle>
			{loading ? (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton small />
				</Skeleton.Repeat>
			) : (
				<>
					{groups?.length ? (
						groups?.map((group, index) => (
							<GroupStructureOffset offset={index} key={group.id}>
								<GroupSidebarItem group={group} onChange={props.onChange} />
							</GroupStructureOffset>
						))
					) : (
						<CollectiveGoalsSidebarSkeleton
							icon={<CityIcon />}
							message={t('goals.team-and-department.section.departments.noItems')}
						/>
					)}
				</>
			)}
		</>
	);
}

function CollectiveGoalsSidebarTeams(props: { onChange: (id: string) => void }) {
	const { t } = useTranslation();
	const { data, loading } = graphql.useViewerGroupsQuery({
		fetchPolicy: 'cache-first',
	});

	const teams = data?.viewer.roles.filter((role) => {
		if (role.targetV2?.__typename === 'Group') {
			return role.targetV2.type === 'TEAM' && !role.targetV2.isArchived;
		}
		return false;
	});

	if (teams?.length === 0) {
		return null;
	}

	return (
		<>
			<Sidebar.SectionTitle mt={4} mb={1}>
				{t('goals.team-and-department.section.teams.title')}
			</Sidebar.SectionTitle>
			{loading ? (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton small />
				</Skeleton.Repeat>
			) : (
				<>
					{teams?.length ? (
						teams?.map((team) => (
							<GroupSidebarItem
								key={team.targetV2!.id}
								group={team.targetV2 as graphql.Group}
								onChange={props.onChange}
							/>
						))
					) : (
						<CollectiveGoalsSidebarSkeleton
							icon={<GroupsIcon />}
							message={t('goals.team-and-department.section.teams.noItems')}
						/>
					)}
				</>
			)}
		</>
	);
}

function GroupGoalsList(props: {
	id: string;
	hideArchived?: boolean;
	onSelect?: (goal: graphql.Goal) => void;
	selectedGoal?: graphql.Goal;
}) {
	const { data } = graphql.useGroupGoalsMetaQuery({
		fetchPolicy: 'cache-first',
		variables: {
			id: props.id,
		},
	});

	if (!data) {
		return <GroupGoals.Skeleton />;
	}

	return (
		<GroupGoals
			hideArchived={props.hideArchived}
			onSelect={props.onSelect}
			selectedGoal={props.selectedGoal}
			group={data.group as graphql.Group}
		/>
	);
}

function CollectiveGoalsSidebarLayout(props: {
	hideArchived?: boolean;
	onSelect?: (goal: graphql.Goal) => void;
	selectedGoal?: graphql.Goal;
	onGroupChange: (id: string) => void;
}) {
	const { groupID } = useContext(CollectiveGoalsContext);
	const { t } = useTranslation();

	const { isGroupManager, isWorkspaceSuperuser, isManager } = useViewerRoles();

	return (
		<SidebarLayout
			sidebarWidth={380}
			sidebar={
				<Sidebar>
					{(isGroupManager || isWorkspaceSuperuser) && (
						<Box mb={4}>
							<Sidebar.SectionTitle mt={2} mb={1}>
								{t('goals.team-and-department.section.advanced.title')}
							</Sidebar.SectionTitle>
							<Sidebar.Item
								title={t('goals.team-and-department.item.organizationChart.title')}
								small
								onClick={() => {
									props.onGroupChange('custom');
								}}
								selected={groupID === 'custom'}
							/>
						</Box>
					)}
					<CollectiveGoalsSidebarDepartments onChange={props.onGroupChange} />
					<CollectiveGoalsSidebarTeams onChange={props.onGroupChange} />
					{isManager && <CollectiveGoalsSidebarByManagement onChange={props.onGroupChange} />}
				</Sidebar>
			}
		>
			<Container>
				{groupID && groupID !== 'custom' && (
					<GroupGoalsList
						id={groupID}
						hideArchived={props.hideArchived}
						onSelect={props.onSelect}
						selectedGoal={props.selectedGoal}
					/>
				)}
				{groupID === 'custom' && (
					<Box my={4}>
						<CollectiveGoalsList
							hideArchived={props.hideArchived}
							onSelect={props.onSelect}
							selectedGoal={props.selectedGoal}
						/>
					</Box>
				)}
			</Container>
		</SidebarLayout>
	);
}

export function StatefulCollectiveGoalsV2(props: {
	hideArchived?: boolean;
	selectedGoal?: graphql.Goal;
	onSelect: (goal: graphql.Goal) => void;
}) {
	const [groupID, setGroupID] = useState<undefined | string>(undefined);

	return (
		<CollectiveGoalsContext.Provider value={{ groupID }}>
			<CollectiveGoalsSidebarLayout
				hideArchived={props.hideArchived}
				selectedGoal={props.selectedGoal}
				onSelect={props.onSelect}
				onGroupChange={setGroupID}
			/>
		</CollectiveGoalsContext.Provider>
	);
}

export default function CollectiveGoalsV2() {
	const { t } = useTranslation();
	const { params } = useRouteMatch<{ id?: string }>();
	const history = useHistory();

	return (
		<>
			<Jumbo.Editable
				hasMediaUpload
				page={PageType.GoalsDepartment}
				title={t('goals.index.title')}
				description={t('goals.index.description')}
				footer={<GoalsTabs />}
			/>
			<Box py={0} width="100%" flexGrow={1}>
				<CollectiveGoalsContext.Provider value={{ groupID: params.id }}>
					<CollectiveGoalsSidebarLayout
						onGroupChange={(id) => {
							history.push(`/goals/collective/${id}`);
						}}
					/>
				</CollectiveGoalsContext.Provider>
			</Box>
		</>
	);
}
